// v0.8 the Friday house auction: one one-of-a-kind item a week, real players bid with cash.
import { useEffect, useRef, useState } from 'react';
import { api } from '../api.ts';
import { useGame, money, fmt, ago } from '../state.tsx';
import { Loading, Panel, useLoad, PlayerLink, ItemIcon, ItemName } from '../components/ui.tsx';
import { StatIcon } from '../art/Scenes.tsx';
import { RarityTag, useTick, countdown } from '../components/v08.tsx';
import { play, sequence } from '../fx/sound.ts';
import { fx, centerOf } from '../fx/fx.tsx';

const when = (t: number) => new Date(t).toLocaleString([], { weekday: 'long', hour: 'numeric', minute: '2-digit' });

export function AuctionTab() {
  const { me, act, byId } = useGame();
  const { data, reload } = useLoad(() => api.get('/api/auction'), [me?.cash], 5_000);
  const [amount, setAmount] = useState('');
  const lastStatus = useRef<string | null>(null);
  const lastTop = useRef<number | null>(null);
  useTick(true);
  const a = data?.auction;
  // Sounds when something changes while you watch: someone bids, the hammer falls.
  useEffect(() => {
    if (!a) return;
    if (lastTop.current !== null && a.topBid !== lastTop.current && !a.mine) play('chips');
    if (lastStatus.current === 'open' && a.status === 'sold') {
      sequence([[0, 'gavel'], [700, a.mine ? 'fanfare' : 'groan']]);
      fx.stamp('Sold!', `${byId.items.get(a.itemId)?.name} · ${money(a.topBid)}${a.mine ? ' · yours' : ''}`, a.mine ? 'gold' : 'bad', 300);
      if (a.mine) fx.coins(24, centerOf(document.querySelector('.auction-lot .item-icon')));
    }
    lastTop.current = a.topBid;
    lastStatus.current = a.status;
  }, [a?.topBid, a?.status]);
  if (!data) return <Loading />;
  if (!a) return <div className="muted">The house hasn’t picked this week’s piece yet. Check back soon.</div>;
  const item = byId.items.get(a.itemId);
  const now = Date.now();
  const n = Math.floor(Number(amount.replace(/[^0-9]/g, '')) || 0);
  const bid = async (value: number) => {
    const r = await act('/api/auction/bid', { amount: value });
    if (!r) return;
    sequence([[0, 'chips'], [200, 'gavel']]);
    fx.float(`−${money(value)} held`, 'loss');
    if (r.extended) fx.stamp('Extended', 'a last-minute bid adds two minutes', 'gold');
    setAmount('');
    reload();
  };
  const open = a.status === 'open';
  return (
    <div>
      <p className="muted" style={{ marginTop: 0 }}>
        Every Friday at 5pm the house auctions off one piece that exists nowhere else in the city. Bidding runs for two hours, and a bid in the
        last two minutes adds two more. The top bid is held by the house; if someone outbids you, your money comes straight back.
        Real players only. {data.poolLeft} of {data.poolTotal} one-of-a-kind pieces are still in the vault.
      </p>
      <div className="auction-lot">
        <div className="lot-art">
          <ItemIcon itemId={a.itemId} size={128} />
          <RarityTag rarity={item?.rarity} />
        </div>
        <div className="grow">
          <div className="lot-name"><ItemName itemId={a.itemId} /></div>
          <div className="small muted" style={{ margin: '2px 0 6px' }}>{item?.description}</div>
          <div className="row" style={{ gap: 14, marginBottom: 8 }}>
            <span><StatIcon kind="attack" /> Attack <b>{item?.attack}</b></span>
            <span><StatIcon kind="defense" /> Defense <b>{item?.defense}</b></span>
            <span className="small dim">{item?.category}</span>
          </div>
          <div className="lot-status">
            {a.status === 'upcoming' && <>Bidding opens <b>{when(a.opensAt)}</b>, in <span className="auction-clock">{countdown(a.opensAt - now)}</span>. Opening bid <span className="money">{money(a.startBid)}</span>.</>}
            {open && <>
              <div>{a.topBid ? <>Top bid <span className="money big-money">{money(a.topBid)}</span> by {a.topBidder && <PlayerLink id={a.topBidder.id} name={a.topBidder.name} />}{a.mine && <b className="good"> (you)</b>}</> : <>No bids yet. Opening bid <span className="money big-money">{money(a.startBid)}</span>.</>}</div>
              <div>Ends in <span className={`auction-clock ${a.endsAt - now < 120_000 ? 'hot' : ''}`}>{countdown(a.endsAt - now)}</span> · {fmt(a.bids)} bid{a.bids === 1 ? '' : 's'}</div>
            </>}
            {a.status === 'sold' && <>Sold to {a.topBidder && <PlayerLink id={a.topBidder.id} name={a.topBidder.name} />} for <span className="money big-money">{money(a.topBid)}</span>.{data.nextOpensAt && <> The next auction opens {when(data.nextOpensAt)}.</>}</>}
            {a.status === 'unsold' && <>Nobody bid, so it went back into the vault.{data.nextOpensAt && <> The next auction opens {when(data.nextOpensAt)}.</>}</>}
          </div>
          {open && !a.mine && (
            <div className="bid-row">
              <button className="btn big red" disabled={me.cash < a.nextMin} onClick={() => bid(a.nextMin)}>Bid {money(a.nextMin)}</button>
              <span className="small dim">or</span>
              <label className="small" htmlFor="bid-amt">your own bid $</label>
              <input id="bid-amt" value={amount} placeholder={String(a.nextMin)} onChange={(e) => setAmount(e.target.value)} style={{ width: 110 }} />
              <button className={`btn ${n >= a.nextMin && n <= me.cash ? '' : 'disabled'}`} onClick={() => bid(n)}>Place bid</button>
              {me.cash < a.nextMin && <div className="small bad" style={{ width: '100%' }}>You need {money(a.nextMin)} cash on hand. Withdraw from the bank first.</div>}
            </div>
          )}
          {open && a.mine && <div className="good" style={{ marginTop: 8 }}><b>You’re the top bidder.</b> Hold on until the hammer falls.</div>}
        </div>
      </div>
      <div className="row" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div className="grow" style={{ minWidth: 260 }}>
          <Panel title="Bids">
            {data.bids.length === 0 ? <div className="muted">No bids yet.</div> : data.bids.map((b: any, i: number) => (
              <div key={i} className="feed-item">
                <span className="grow"><PlayerLink id={b.playerId} name={b.name} /> bid <span className="money">{money(b.amount)}</span></span>
                <span className="when">{ago(b.at)}</span>
              </div>
            ))}
          </Panel>
        </div>
        <div className="grow" style={{ minWidth: 260 }}>
          <Panel title="Past auctions">
            {data.past.length === 0 ? <div className="muted">This is the first one.</div> : data.past.map((p: any, i: number) => (
              <div key={i} className="feed-item" style={{ alignItems: 'center' }}>
                <ItemIcon itemId={p.itemId} size={28} />
                <span className="grow"><ItemName itemId={p.itemId} /><div className="small">{p.status === 'sold' ? <>to {p.winner} for <span className="money">{money(p.price)}</span></> : <span className="dim">no bids</span>}</div></span>
              </div>
            ))}
          </Panel>
        </div>
      </div>
    </div>
  );
}
